import type React from 'react'
import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSales, useCreateSale, useUpdateSale } from '../../hooks/useSales'
import { useSettings } from '../../hooks/useSettings'
import { useProducts } from '../../hooks/useProducts'
import { BackLink } from '../../components/BackLink'

interface LineItemDraft {
  productId: string
  quantity: string
  unitPrice: string
}

export default function SaleForm() {
  const navigate = useNavigate()
  const { id } = useParams<{ id?: string }>()
  const isEdit = Boolean(id)

  const { data: settings } = useSettings()
  const { data: existingSales } = useSales()
  const createSale = useCreateSale()
  const updateSale = useUpdateSale()

  const existing = existingSales?.find((s) => s.id === id)

  const today = new Date().toISOString().slice(0, 10)

  const [saleDate, setSaleDate] = useState(existing?.sale_date ?? today)
  const [grossAmount, setGrossAmount] = useState(existing ? String(existing.gross_amount) : '')
  const [platformFees, setPlatformFees] = useState(existing ? String(existing.platform_fees) : '0')
  const [shippingCharged, setShippingCharged] = useState(existing ? String(existing.shipping_charged) : '0')
  const [shippingCost, setShippingCost] = useState(existing ? String(existing.shipping_cost) : '0')
  const [platform, setPlatform] = useState<string>(existing?.platform ?? settings?.default_platform ?? 'etsy')
  const [customerName, setCustomerName] = useState(existing?.customer_name ?? '')
  const [notes, setNotes] = useState(existing?.notes ?? '')
  const [errorMessage, setErrorMessage] = useState('')
  const [lineItems, setLineItems] = useState<LineItemDraft[]>([])
  const { data: products } = useProducts()

  const handleGrossChange = (v: string) => {
    setGrossAmount(v)
    if (!settings) return
    const n = Number(v)
    if (Number.isNaN(n) || n <= 0) return
    if (platform === 'etsy') {
      setPlatformFees((n * Number(settings.default_etsy_fee_pct) / 100).toFixed(2))
    } else if (platform === 'shopify') {
      setPlatformFees((n * Number(settings.default_shopify_fee_pct) / 100).toFixed(2))
    }
  }

  const handlePlatformChange = (v: string) => {
    setPlatform(v)
    if (!settings) return
    const n = Number(grossAmount)
    if (Number.isNaN(n) || n <= 0) return
    if (v === 'etsy') {
      setPlatformFees((n * Number(settings.default_etsy_fee_pct) / 100).toFixed(2))
    } else if (v === 'shopify') {
      setPlatformFees((n * Number(settings.default_shopify_fee_pct) / 100).toFixed(2))
    } else {
      setPlatformFees('0')
    }
  }

  const addLineItem = () =>
    setLineItems([...lineItems, { productId: '', quantity: '1', unitPrice: '' }])
  const removeLineItem = (idx: number) =>
    setLineItems(lineItems.filter((_, i) => i !== idx))
  const updateLineItem = (idx: number, key: keyof LineItemDraft, val: string) =>
    setLineItems(lineItems.map((li, i) => {
      if (i !== idx) return li
      const updated = { ...li, [key]: val }
      // When switching product, prefill unit_price from product's default if not already set
      if (key === 'productId' && val) {
        const product = (products ?? []).find((p) => p.id === val)
        if (product?.default_unit_price != null && !updated.unitPrice) {
          updated.unitPrice = String(product.default_unit_price)
        }
      }
      return updated
    }))

  // Auto-sync grossAmount when line items exist
  const lineItemsTotal = lineItems.reduce(
    (sum, li) => sum + (Number(li.quantity) || 0) * (Number(li.unitPrice) || 0),
    0,
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorMessage('')

    if (isEdit && id) {
      try {
        await updateSale.mutateAsync({
          id,
          sale_date: saleDate,
          gross_amount: Number(grossAmount),
          platform_fees: Number(platformFees),
          shipping_charged: Number(shippingCharged),
          shipping_cost: Number(shippingCost),
          platform,
          customer_name: customerName || null,
          notes: notes || null,
        })
        navigate('/money')
      } catch (err) {
        setErrorMessage((err as Error).message)
      }
      return
    }

    try {
      // Use computed lineItemsTotal if line items are present, otherwise use grossAmount input
      const effectiveGross = lineItems.length > 0 ? lineItemsTotal : Number(grossAmount)

      const validLineItems = lineItems
        .filter((li) => li.productId && Number(li.quantity) > 0 && Number(li.unitPrice) > 0)
        .map((li) => ({
          product_id: li.productId,
          quantity: Number(li.quantity),
          unit_price: Number(li.unitPrice),
        }))

      await createSale.mutateAsync({
        sale: {
          sale_date: saleDate,
          gross_amount: effectiveGross,
          platform_fees: Number(platformFees),
          shipping_charged: Number(shippingCharged),
          shipping_cost: Number(shippingCost),
          platform,
          customer_name: customerName || null,
          notes: notes || null,
        },
        lineItems: validLineItems,
      })
      navigate('/money')
    } catch (err) {
      setErrorMessage((err as Error).message)
    }
  }

  const isPending = isEdit ? updateSale.isPending : createSale.isPending

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 560 }}>
      <BackLink fallback="/money" />
      <h1>{isEdit ? 'Edit sale' : 'Log a sale'}</h1>
      <form onSubmit={handleSubmit} style={{ marginTop: 'var(--space-6)' }}>
        <Field label="Date" value={saleDate} onChange={setSaleDate} type="date" required />
        <Field label="Gross amount ($)" value={grossAmount} onChange={handleGrossChange} type="decimal" required />

        {isEdit ? (
          <div
            style={{
              marginBottom: 'var(--space-4)',
              padding: 'var(--space-3)',
              background: 'var(--color-surface-2)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-faint)',
              fontStyle: 'italic',
            }}
          >
            Line items can&apos;t be changed after a sale is logged. Delete and re-create to fix.
          </div>
        ) : (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <label
              style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                marginBottom: 'var(--space-2)',
              }}
            >
              Line items (optional)
            </label>
            {lineItems.map((li, idx) => (
              <div
                key={idx}
                style={{
                  display: 'flex',
                  gap: 'var(--space-2)',
                  marginBottom: 'var(--space-2)',
                }}
              >
                <select
                  value={li.productId}
                  onChange={(e) => updateLineItem(idx, 'productId', e.target.value)}
                  style={{
                    flex: 2,
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg)',
                    color: 'var(--color-text)',
                  }}
                >
                  <option value="">Select product…</option>
                  {(products ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="qty"
                  value={li.quantity}
                  onChange={(e) => updateLineItem(idx, 'quantity', e.target.value)}
                  style={{
                    width: 60,
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg)',
                    color: 'var(--color-text)',
                  }}
                />
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="$ each"
                  value={li.unitPrice}
                  onChange={(e) => updateLineItem(idx, 'unitPrice', e.target.value)}
                  style={{
                    width: 80,
                    padding: 'var(--space-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-bg)',
                    color: 'var(--color-text)',
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeLineItem(idx)}
                  style={{ color: 'var(--color-danger)', padding: '0 var(--space-2)' }}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addLineItem}
              style={{
                color: 'var(--color-primary)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
              }}
            >
              + Add line item
            </button>
            {lineItems.length > 0 && (
              <div
                style={{
                  marginTop: 'var(--space-2)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-faint)',
                }}
              >
                Line items total: <strong>${lineItemsTotal.toFixed(2)}</strong> — overrides
                the gross amount above when present.
              </div>
            )}
          </div>
        )}

        <Field
          label="Platform"
          value={platform}
          onChange={handlePlatformChange}
          select={['etsy', 'shopify', 'local-cash', 'friend', 'other']}
        />
        <Field label="Platform fees ($)" value={platformFees} onChange={setPlatformFees} type="decimal" hint="Auto-filled from your saved default % when applicable" />
        <Field label="Shipping charged ($)" value={shippingCharged} onChange={setShippingCharged} type="decimal" />
        <Field label="Shipping cost ($)" value={shippingCost} onChange={setShippingCost} type="decimal" />
        <Field label="Customer (optional)" value={customerName} onChange={setCustomerName} />
        <Field label="Notes (optional)" value={notes} onChange={setNotes} multiline />

        <button
          type="submit"
          disabled={isPending}
          style={{
            marginTop: 'var(--space-6)',
            padding: 'var(--space-3) var(--space-5)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
          }}
        >
          {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Save sale'}
        </button>

        {errorMessage && (
          <p style={{ marginTop: 'var(--space-3)', color: 'var(--color-danger)', fontSize: 'var(--font-size-sm)' }}>
            {errorMessage}
          </p>
        )}
      </form>
    </div>
  )
}

function Field({ label, value, onChange, type = 'text', hint, required, multiline, select }: {
  label: string
  value: string
  onChange: (v: string) => void
  type?: 'text' | 'date' | 'decimal'
  hint?: string
  required?: boolean
  multiline?: boolean
  select?: string[]
}) {
  const commonStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: 320,
    padding: 'var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
  }
  const textareaStyle: React.CSSProperties = {
    width: '100%',
    padding: 'var(--space-3)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg)',
    color: 'var(--color-text)',
  }
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <label style={{ display: 'block', fontSize: 'var(--font-size-sm)', fontWeight: 600, marginBottom: 'var(--space-1)' }}>
        {label} {required && <span style={{ color: 'var(--color-danger)' }}>*</span>}
      </label>
      {select ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} style={commonStyle}>
          {select.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      ) : multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3} style={textareaStyle} />
      ) : (
        <input
          type={type === 'date' ? 'date' : 'text'}
          inputMode={type === 'decimal' ? 'decimal' : undefined}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={commonStyle}
        />
      )}
      {hint && <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)', marginTop: 'var(--space-1)' }}>{hint}</p>}
    </div>
  )
}
